// src/components/roadmap/EditRoadmapItemDialog.jsx
import React, { useEffect, useMemo, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "components/ui/dialog";
import { Button } from "components/ui/button";
import { Input } from "components/ui/input";
import { Textarea } from "components/ui/textarea";
import { Label } from "components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "components/ui/select";
import { Badge } from "components/ui/badge";
import { X, Edit } from "lucide-react";
import { okrsService } from "../../services/database";

export default function EditRoadmapItemDialog({ open, onClose, onSubmit, problems = [], item }) {
  const blank = {
    title: "",
    description: "",
    expected_impact: "",
    success_metric: "",
    hypothesis: "",
    risks: "",
    problem_id: "",
    okr_ref: "",
    time_horizon: "Next",
    status: "planned",
    owner: "",
    impact: 3,
    progress: 0,
    target_date: "",
    tags: [],
  };

  const [form, setForm] = useState(blank);
  const [newTag, setNewTag] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [okrs, setOkrs] = useState([]);

  // Load OKRs when dialog opens
  useEffect(() => {
    if (!open) return;

    const loadOkrs = async () => {
      try {
        const okrData = await okrsService.list("-updated_at", 100);
        setOkrs(okrData);
      } catch (error) {
        console.error("Failed to load OKRs:", error);
        setOkrs([]);
      }
    };

    loadOkrs();
  }, [open]);

  // Initialize form with item data when dialog opens or item changes
  useEffect(() => {
    if (!open || !item) return;
    setForm({
      title: item.title || "",
      description: item.description || "",
      expected_impact: item.expected_impact || "",
      success_metric: item.success_metric || "",
      hypothesis: item.hypothesis || "",
      risks: item.risks || "",
      problem_id: item.problem_id ? String(item.problem_id) : "",
      okr_ref: item.okr_ref ? String(item.okr_ref) : "",
      time_horizon: item.time_horizon || "Next",
      status: item.status || "planned",
      owner: item.owner || "",
      impact: Number(item.impact || item.expected_impact) || 3,
      progress: Number(item.progress || item.progress_percentage) || 0,
      target_date: item.target_date || "",
      tags: Array.isArray(item.tags) ? item.tags : [],
    });
    setNewTag("");
  }, [open, item]);

  const problemsOptions = useMemo(
    () => problems.map((p) => ({ id: String(p.id), label: p.title })),
    [problems]
  );

  const okrOptions = useMemo(() => {
    return okrs.map((okr) => ({
      id: String(okr.id),
      label: okr.title,
      quarter: okr.quarter,
      year: okr.year,
      status: okr.status
    }));
  }, [okrs]);

  const addTag = () => {
    const t = newTag.trim();
    if (t && !form.tags.includes(t)) {
      setForm((prev) => ({ ...prev, tags: [...prev.tags, t] }));
    }
    setNewTag("");
  };

  const removeTag = (t) => setForm((prev) => ({ ...prev, tags: prev.tags.filter((x) => x !== t) }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError("");
    try {
      await Promise.resolve(onSubmit?.(item.id, { ...form, problem_card_ids: [...new Set([...(item.problem_card_ids || []).slice(1), ...(form.problem_id ? [form.problem_id] : [])])] }));
      setNewTag("");
    } catch (err) {
      setError(err.message || "Could not save initiative.");
    }
    setSubmitting(false);
  };

  if (!item) return null;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent>
        <div style={{ maxWidth: 780, maxHeight: "90vh", overflowY: "auto" }}>
          <DialogHeader>
            <DialogTitle style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 18, fontWeight: 800, color: "var(--fg)", marginBottom: 8 }}>
              <Edit size={20} color="var(--accent)" />
              Edit Roadmap Initiative
            </DialogTitle>
          </DialogHeader>

          <form onSubmit={handleSubmit} style={{ display: "grid", gap: 16 }}>
            {error && <p role="alert" className="v2-error">{error}</p>}
            <div>
              <Label>Title *</Label>
              <Input
                required
                placeholder="Short initiative title"
                value={form.title}
                onChange={(e) => setForm((p) => ({ ...p, title: e.target.value }))}
                style={{ marginTop: 6 }}
              />
            </div>

            <div>
              <Label>Description</Label>
              <Textarea
                placeholder="What solution is proposed and what outcome is expected?"
                value={form.description}
                onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))}
                style={{ marginTop: 6, minHeight: 96 }}
              />
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              <div>
                <Label>Linked Problem (optional)</Label>
                <Select
                  value={form.problem_id}
                  onValueChange={(v) => setForm((p) => ({ ...p, problem_id: v }))}
                >
                  <SelectTrigger aria-label="Linked Problem" style={{ marginTop: 6 }}>
                    <SelectValue placeholder="Select a problem…" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="">None</SelectItem>
                    {problemsOptions.map((opt) => (
                      <SelectItem key={opt.id} value={opt.id}>{opt.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Linked OKR (optional)</Label>
                <Select
                  value={form.okr_ref}
                  onValueChange={(v) => setForm((p) => ({ ...p, okr_ref: v }))}
                >
                  <SelectTrigger aria-label="Linked OKR" style={{ marginTop: 6 }}>
                    <SelectValue placeholder="Select an OKR…" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="">None</SelectItem>
                    {okrOptions.map((okr) => (
                      <SelectItem key={okr.id} value={okr.id}>
                        {okr.label} ({okr.quarter} {okr.year})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="v2-form-grid">
              <label className="v2-field"><span>Expected outcome</span><input value={form.expected_impact} onChange={e => setForm(p => ({ ...p, expected_impact: e.target.value }))} placeholder="What should change for the customer?" /></label>
              <label className="v2-field"><span>Success metric</span><input value={form.success_metric} onChange={e => setForm(p => ({ ...p, success_metric: e.target.value }))} placeholder="Metric and target" /></label>
              <label className="v2-field"><span>Hypothesis</span><textarea value={form.hypothesis} onChange={e => setForm(p => ({ ...p, hypothesis: e.target.value }))} placeholder="We believe this change will?" /></label>
              <label className="v2-field"><span>Known risks</span><textarea value={form.risks} onChange={e => setForm(p => ({ ...p, risks: e.target.value }))} placeholder="What could make this fail or cause harm?" /></label>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 }}>
              <div>
                <Label>Horizon</Label>
                <Select
                  value={form.time_horizon}
                  onValueChange={(v) => setForm((p) => ({ ...p, time_horizon: v }))}
                >
                  <SelectTrigger style={{ marginTop: 6 }}>
                    <SelectValue placeholder="Select horizon" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Now">Now</SelectItem>
                    <SelectItem value="Next">Next</SelectItem>
                    <SelectItem value="Later">Later</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Status</Label>
                <Select
                  value={form.status}
                  onValueChange={(v) => setForm((p) => ({ ...p, status: v }))}
                >
                  <SelectTrigger style={{ marginTop: 6 }}>
                    <SelectValue placeholder="Select status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="draft">Draft</SelectItem>
                    <SelectItem value="planned">Planned</SelectItem>
                    <SelectItem value="in_progress">In Progress</SelectItem>
                    <SelectItem value="done">Done</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Owner</Label>
                <Input
                  placeholder="Name / team"
                  value={form.owner}
                  onChange={(e) => setForm((p) => ({ ...p, owner: e.target.value }))}
                  style={{ marginTop: 6 }}
                />
              </div>
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              <div>
                <Label>Impact (1–5)</Label>
                <Input
                  type="number"
                  min={1}
                  max={5}
                  value={form.impact}
                  onChange={(e) => setForm((p) => ({ ...p, impact: Number(e.target.value || 0) }))}
                  style={{ marginTop: 6 }}
                />
              </div>
              <div>
                <Label>Progress (%)</Label>
                <Input
                  type="number"
                  min={0}
                  max={100}
                  value={form.progress}
                  onChange={(e) => setForm((p) => ({ ...p, progress: Number(e.target.value || 0) }))}
                  style={{ marginTop: 6 }}
                />
              </div>
            </div>

            <div>
              <Label>Target Date (for reminders)</Label>
              <Input
                type="date"
                value={form.target_date}
                onChange={(e) => setForm((p) => ({ ...p, target_date: e.target.value }))}
                style={{ marginTop: 6 }}
              />
            </div>

            {/* Tags */}
            <div>
              <Label>Tags</Label>
              <div style={{ marginTop: 6, display: "grid", gap: 8 }}>
                <div style={{ display: "flex", gap: 8 }}>
                  <Input
                    value={newTag}
                    placeholder="Add a tag..."
                    onChange={(e) => setNewTag(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        addTag();
                      }
                    }}
                    style={{ flex: 1 }}
                  />
                  <Button type="button" onClick={addTag}>Add</Button>
                </div>

                {form.tags.length > 0 && (
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                    {form.tags.map((tag) => (
                      <Badge
                        key={tag}
                        className="text-xs"
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          gap: 6,
                          background: "var(--bg-subtle)",
                          color: "var(--fg-secondary)",
                          border: 0,
                        }}
                      >
                        {tag}
                        <button
                          type="button"
                          onClick={() => removeTag(tag)}
                          title="Remove"
                          style={{
                            border: 0,
                            background: "transparent",
                            padding: 0,
                            display: "inline-flex",
                            alignItems: "center",
                            cursor: "pointer",
                          }}
                        >
                          <X size={12} />
                        </button>
                      </Badge>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, paddingTop: 12, borderTop: "1px solid var(--border)" }}>
              <Button type="button" onClick={onClose} disabled={submitting}>Cancel</Button>
              <Button type="submit" disabled={submitting} variant="primary">
                {submitting ? "Updating..." : "Update Initiative"}
              </Button>
            </div>
          </form>
        </div>
      </DialogContent>
    </Dialog>
  );
}